import DOMPurify from '@packages/config/utils/dompurify';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { useState } from 'react';
import reactStringReplace from 'react-string-replace';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import type { ModalType } from '../../../interfaces/components';

type ComponentProps = {
  /** replace placeholders within a string. Example: "Prices incl. VAT and plus [[TextLink]]." */
  replaceText: string;
  /** data object */
  data: ModalType;
};

/** Composite dialog component with title and content that is initialized by the Component "ModalByTextlink". Based on Shared Dialog: @packages/shared/Dialog */
export const ModalChild = ({ replaceText, data }: ComponentProps) => {
  const {
    field_placeholder_id: placeholderId,
    field_placeholder_text: placeholderText,
    field_headline: headline = '',
    field_html_editor: modalContent = '',
  } = data;

  // init state
  const [stateOpen, setStateOpen] = useState<boolean>(false);

  // handle open/close
  const handleOpen = () => setStateOpen(true);
  const handleClose = () => setStateOpen(false);

  return (
    <div>
      {reactStringReplace(replaceText, placeholderId, () => (
        <>
          <Button
            key={placeholderId}
            variant="link"
            size="small"
            className="bg-color-transparent! text-color-primary-contrasttext border-0 p-0"
            onClick={(event) => {
              event.preventDefault();
              handleOpen();
            }}
          >
            <SoftHyphenInjector text={placeholderText} />
          </Button>
          <Dialog isOpen={stateOpen} onClose={handleClose}>
            <DialogHeader onClose={handleClose}>{headline}</DialogHeader>
            <DialogContent
              className="text-size-body2 max-h-[85vh] overflow-y-auto p-2"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalContent) }}
            />
          </Dialog>
        </>
      )).map((chunk, index) =>
        // iterate over the returned array and render the chunks (that stayed a string) as html
        typeof chunk === 'string' ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(chunk) }} />
        ) : (
          chunk
        ),
      )}
    </div>
  );
};
